import {
  Heading,
  Text,
  Icon,
  modularScale,
  palette,
  media,
} from "@ottatech/design";
import React, { createContext, useContext, useRef } from "react";
import styled, { css } from "styled-components";
import { useLocalStorage } from "react-use";
import { AnimationProps, motion, useReducedMotionConfig } from "framer-motion";

/*
 * An accessible animated expander component. Use as follows:
 * import * as Expander from 'components/Expander'
 *
 * <Expander.Context>
 *   <Expander.Content>
 *     Something to show/hide
 *   </Expander.Content>
 *   <Expander.Toggle/>
 * </Expander.Context>
 */

enum ExpansionDirection {
  Up = "UP",
  Down = "DOWN",
}

type ExpanderCtxProps = {
  setExpanded(expanded: boolean): void;
  expanded: boolean;
  id: string;
};

const ExpanderCtx = createContext<ExpanderCtxProps>({
  setExpanded: () => null,
  expanded: false,
  id: "",
});

type ButtonProps = {
  className?: string;
  fontSize?: number;
  ctaMessages?: { expanded: string; contracted: string };
  useHeadingFont?: boolean;
  content?: React.ReactElement;
  spaceBetweenOnMobile?: boolean;
};

interface ExpandingContentProps {
  className?: string;
  "data-testid"?: string;
  direction?: ExpansionDirection;
  displayType?: string;
}

const TextButton = styled.button`
  color: ${palette.brand.black};
  background: transparent;
  cursor: pointer;
  display: block;
  margin: auto;
  border: none;
`;

const StyledHeading = styled(Heading)<{ fontSize: number }>`
  ${({ fontSize }) => css`
    font-size: ${modularScale(fontSize - 1)};

    ${media({ from: "tablet" })`
    font-size: ${modularScale(fontSize)};
  `}
  `}
`;

const StyledCTA = styled.div<{ spaceBetweenOnMobile: boolean }>`
  display: flex;
  justify-content: ${({ spaceBetweenOnMobile }) =>
    spaceBetweenOnMobile ? "space-between" : "center"};
  width: 100%;

  i {
    margin-left: ${modularScale(-6)};
  }

  ${media({ from: "tablet" })`
    justify-content: center;

    i {
      margin-left: ${modularScale(-4)};
    }
  `}
`;

const MotionDiv = styled(motion.div)<{
  direction: ExpansionDirection;
}>`
  overflow: hidden;

  ${({ direction }) =>
    direction === ExpansionDirection.Up &&
    css`
      bottom: 0;
    `};
`;

/**
 * Element that creates a new expansion context.
 * The expectation is there will be exactly one Content and one Toggle component
 * nested within the provided child elements.
 */
export function Context({
  id,
  children,
  initial = false,
}: React.PropsWithChildren<{
  id: string;
  initial?: boolean;
}>): React.ReactElement {
  const [expanded, setExpanded] = useLocalStorage(`expander-${id}`, initial);

  return (
    <ExpanderCtx.Provider
      value={{ expanded: expanded ?? initial, setExpanded, id }}
    >
      {children}
    </ExpanderCtx.Provider>
  );
}

function showHide(
  expanded: boolean,
  displayType: string
): AnimationProps["animate"] {
  return expanded
    ? {
        height: "auto",
        margin: undefined,
        display: displayType,
      }
    : {
        height: 0,
        margin: 0,
        transitionEnd: {
          display: "none",
        },
      };
}

/**
 * A component that either shows or hides its children depending on the expanded prop.
 * We do some gymnastics with refs to smoothly animate the height between 0 and auto.
 */
export function Content({
  children,
  className,
  "data-testid": testId,
  direction = ExpansionDirection.Down,
  displayType = "block",
}: React.PropsWithChildren<ExpandingContentProps>): React.ReactElement {
  const reduced = useReducedMotionConfig();
  const { expanded, id } = useContext(ExpanderCtx);
  const previouslyExpanded = useRef<boolean>(expanded);
  const ref = useRef<HTMLDivElement>(null);

  /**
   * When the animation ends, focus on the content if expanded
   */
  function onAnimationComplete() {
    if (expanded && !previouslyExpanded.current) {
      ref.current?.querySelector("a")?.focus();
    }
    previouslyExpanded.current = expanded;
  }

  return (
    <MotionDiv
      transition={{ duration: reduced ? 0 : 0.1, ease: "easeInOut" }}
      onAnimationComplete={() => onAnimationComplete()}
      animate={showHide(expanded, displayType)}
      aria-hidden={!expanded}
      className={className}
      data-testid={testId}
      id={id}
      direction={direction}
    >
      <div ref={ref}>{children}</div>
    </MotionDiv>
  );
}

/**
 * A button to toggle whether something is expanded or not
 * presented as a text link but is actually a button for accessibility
 */

const FormattedCTA = ({
  fontType,
  text,
  fontSize,
}: {
  fontType: string;
  text: string;
  fontSize: number;
}): React.ReactElement => {
  switch (fontType) {
    case "HEADING":
      return <StyledHeading fontSize={fontSize}>{text}</StyledHeading>;
    default:
      return <Text size={fontSize}>{text}</Text>;
  }
};

export function Toggle({
  className,
  fontSize = 0,
  ctaMessages = { expanded: "See less", contracted: "See more" },
  useHeadingFont = false,
  spaceBetweenOnMobile = false,
}: ButtonProps): React.ReactElement {
  const { expanded, setExpanded, id } = useContext(ExpanderCtx);

  // These take the ctas for each state, lower-case them, and replace any spaces with hyphens.
  const expandedDataId = `${id}-${ctaMessages.expanded
    .toLowerCase()
    .replace(/\s+/g, "-")}`;
  const contractedDataId = `${id}-${ctaMessages.contracted
    .toLowerCase()
    .replace(/\s+/g, "-")}`;

  return (
    <>
      <TextButton
        aria-controls={id}
        aria-expanded={`${expanded}`}
        onClick={() => setExpanded(!expanded)}
        data-analytics-id={expanded ? expandedDataId : contractedDataId}
        className={className}
      >
        <StyledCTA spaceBetweenOnMobile={spaceBetweenOnMobile}>
          <FormattedCTA
            text={expanded ? ctaMessages.expanded : ctaMessages.contracted}
            fontType={useHeadingFont ? "HEADING" : "REGULAR"}
            fontSize={fontSize}
          />

          <Icon
            icon={expanded ? "chevron-up" : "chevron-down"}
            size={!fontSize ? 1 : fontSize}
          />
        </StyledCTA>
      </TextButton>
    </>
  );
}
